<template>
  <!-- Loading skeleton -->
  <section v-if="loading" class="animate-pulse">
    <div class="h-7 bg-gray-700 rounded w-24 mb-2"></div>
    <div v-for="i in SKELETON_ROWS" :key="i" class="flex justify-between py-2 border-b border-gray-700">
      <div class="h-4 bg-gray-700 rounded w-40"></div>
      <div class="h-4 bg-gray-700 rounded w-20"></div>
    </div>
  </section>

  <section v-else-if="sessions.length">
    <h2>Sessions</h2>
    <table class="w-full text-sm">
      <tbody>
        <tr
          v-for="session in paginatedSessions"
          :key="session.id"
          class="border-b border-gray-700"
        >
          <td class="py-1">
            <RouterLink
              :to="`/astronomers/${props.slug}/sessions/${session.id}`"
              class="text-yellow-300"
            >
              {{ session.target.name }}
            </RouterLink>
          </td>
          <td class="py-1 text-right text-gray-400 text-xs">
            {{ timeAgo(session.session_ended_at) }} ago
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="totalPages > 1" class="flex items-center justify-center gap-4 mt-3 text-sm">
      <button
        class="btn"
        :disabled="currentPage === 1"
        @click="currentPage--"
      >
        &laquo; Prev
      </button>
      <span class="text-gray-400">{{ currentPage }} / {{ totalPages }}</span>
      <button
        class="btn"
        :disabled="currentPage === totalPages"
        @click="currentPage++"
      >
        Next &raquo;
      </button>
    </div>

    <div v-if="canEdit" class="text-right text-xs mt-2">
      <a href="/session_log_files/new" class="text-yellow-300">
        Upload offline session log
      </a>
      ✶
      <a :href="`/astronomers/${props.slug}/export.csv`" class="text-yellow-300">
        Download raw data
      </a>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { apiFetch } from '../utils/api'
import { timeAgo } from '../utils/timeAgo'

const PER_PAGE = 25
const SKELETON_ROWS = 5

const props = defineProps<{ slug: string }>()

interface Session {
  id: number
  session_ended_at: string
  target: { id: number; name: string }
}

interface StatsResponse {
  can_edit: boolean
  sessions: Session[]
}

const sessions = ref<Session[]>([])
const canEdit = ref(false)
const loading = ref(true)
const currentPage = ref(1)

const totalPages = computed(() => Math.ceil(sessions.value.length / PER_PAGE))
const paginatedSessions = computed(() => {
  const start = (currentPage.value - 1) * PER_PAGE
  return sessions.value.slice(start, start + PER_PAGE)
})

onMounted(async () => {
  try {
    const data = await apiFetch(`/api/spa/astronomers/${props.slug}/stats`) as StatsResponse
    sessions.value = data.sessions
    canEdit.value = data.can_edit
  } catch {
    // section stays hidden on failure
  } finally {
    loading.value = false
  }
})
</script>
